/**
 * =============================================================================
 * IDS CRM Web - Login Page
 * =============================================================================
 *
 * PURPOSE:
 * Login form for user authentication. Redirects to dashboard on success.
 *
 * =============================================================================
 */

import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth, API_URL } from '../context/AuthContext';
import { beginMicrosoftLogin } from '../utils/ssoFlow';
import './Login.css';

function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  // null = still asking the API; {enabled:false} = password login (local/dev);
  // {enabled:true,...} = Microsoft-only (Gareth 2026-08-04: no password fallback).
  const [ssoConfig, setSsoConfig] = useState(null);

  const { login } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    // A failed SSO round-trip leaves its reason here (see AuthCallback).
    const ssoError = sessionStorage.getItem('ids-crm-sso-error');
    if (ssoError) {
      sessionStorage.removeItem('ids-crm-sso-error');
      setError(ssoError);
    }

    let cancelled = false;
    (async () => {
      try {
        const response = await fetch(`${API_URL}/api/v1/auth/sso-config`);
        const config = await response.json();
        if (!cancelled) setSsoConfig(config.enabled ? config : { enabled: false });
      } catch (err) {
        // API unreachable or old build — fall back to the password form.
        if (!cancelled) setSsoConfig({ enabled: false });
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const handleMicrosoftLogin = async () => {
    setError('');
    setLoading(true);
    try {
      await beginMicrosoftLogin(ssoConfig);
    } catch (err) {
      setError(err.message || 'Could not start Microsoft sign-in.');
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await login(email, password);
      navigate('/');
    } catch (err) {
      setError(err.message || 'Login failed. Please check your credentials.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-container">
      <div className="login-brand" role="img" aria-label="IDS - Intelligent Dairy Solutions" />
      <div className="login-card">
        <div className="login-header">
          <h1>Sign in</h1>
          <p>Customers, pipeline, service and scheduling in one place.</p>
        </div>

        {ssoConfig === null ? (
          <div className="login-form">
            {error && <div className="login-error">{error}</div>}
            <p className="login-loading-note">Checking sign-in options…</p>
          </div>
        ) : ssoConfig.enabled ? (
          <div className="login-form">
            {error && <div className="login-error">{error}</div>}
            <button
              type="button"
              className="login-button login-microsoft"
              onClick={handleMicrosoftLogin}
              disabled={loading}
            >
              <svg width="18" height="18" viewBox="0 0 21 21" aria-hidden="true">
                <rect x="1" y="1" width="9" height="9" fill="#f25022" />
                <rect x="11" y="1" width="9" height="9" fill="#7fba00" />
                <rect x="1" y="11" width="9" height="9" fill="#00a4ef" />
                <rect x="11" y="11" width="9" height="9" fill="#ffb900" />
              </svg>
              {loading ? 'Redirecting…' : 'Sign in with Microsoft'}
            </button>
            <p className="login-sso-note">Use your company Microsoft account.</p>
          </div>
        ) : (
        <form onSubmit={handleSubmit} className="login-form">
          {error && (
            <div className="login-error">
              {error}
            </div>
          )}

          <div className="form-group">
            <label htmlFor="email">Email</label>
            <input
              type="email"
              id="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Enter your email"
              required
              autoFocus
              autoComplete="email"
            />
          </div>

          <div className="form-group">
            <label htmlFor="password">Password</label>
            <input
              type="password"
              id="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Enter your password"
              required
              autoComplete="current-password"
            />
          </div>

          <button
            type="submit"
            className="login-button"
            disabled={loading}
          >
            {loading ? 'Signing in...' : 'Sign In'}
          </button>
        </form>
        )}
      </div>
    </div>
  );
}

export default Login;
